import type { Predication } from '@/types/Predication'
import type { MembreEquipe } from '@/types/MembreEquipe'
import type { Contact, Dons, MotDuPasteur, SiteContent } from '@/types/Contenu'
import { nouvelIdentifiant } from '@/helpers/identifiants'
import { DEFAULT_CONTENT } from './defaultContent'
import { API_URL } from './auth'
import { creerPredication, modifierPredication, supprimerPredication } from './predicationsApi'
import {
  changerPosition,
  creerMembre,
  enregistrerContact,
  enregistrerDons,
  enregistrerMotDuPasteur,
  lireContenu,
  modifierMembre,
  supprimerMembre,
} from './contenuApi'

/**
 * Point d'accès unique au contenu modifiable du site.
 *
 * Deux implémentations : l'API Symfony (si VITE_API_URL est défini), sinon le mode
 * démonstration qui enregistre tout dans le navigateur.
 */
export interface ContentRepository {
  /** Contenu disponible immédiatement, sans réseau (cache ou stockage local). null : premier passage. */
  contenuImmediat(): SiteContent | null
  /** Contenu à jour. */
  getContent(): Promise<SiteContent>
  /** Crée la prédication si son id est vide, sinon la modifie. Renvoie la version enregistrée. */
  savePredication(predication: Predication): Promise<Predication>
  deletePredication(id: string): Promise<void>
  /** Crée le membre si son id est vide (en dernière position), sinon le modifie. */
  saveMembre(membre: MembreEquipe): Promise<MembreEquipe>
  deleteMembre(id: string): Promise<void>
  /** Enregistre l'ordre d'affichage de l'équipe (liste des identifiants). */
  reorderEquipe(ids: string[]): Promise<void>
  saveMotDuPasteur(mot: MotDuPasteur): Promise<MotDuPasteur>
  saveDons(dons: Dons): Promise<Dons>
  saveContact(contact: Contact): Promise<Contact>
  /** Revient au contenu d'origine (mode démonstration uniquement). */
  reset(): Promise<SiteContent>
}

const STORAGE_KEY = 'cmp-contenu-v1'

/**
 * Mode démonstration : les modifications sont enregistrées dans le navigateur
 * (localStorage) et ne sont visibles que sur l'appareil qui les a faites.
 */
function createLocalRepository(): ContentRepository {
  const read = (): SiteContent => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) return { ...DEFAULT_CONTENT, ...(JSON.parse(raw) as Partial<SiteContent>) }
    } catch {
      // Stockage indisponible ou données corrompues — on retombe sur le contenu d'origine.
    }
    return DEFAULT_CONTENT
  }

  const write = (content: SiteContent) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(content))
  }

  const update = (patch: (content: SiteContent) => SiteContent) => write(patch(read()))

  const upsert = <T extends { id: string }>(list: T[], item: T) =>
    list.some((x) => x.id === item.id) ? list.map((x) => (x.id === item.id ? item : x)) : [...list, item]

  return {
    async getContent() {
      return read()
    },
    contenuImmediat() {
      return read()
    },
    async savePredication(p) {
      const predication = p.id ? p : { ...p, id: nouvelIdentifiant(p.titre, read().predications.map((x) => x.id)) }
      update((c) => ({ ...c, predications: upsert(c.predications, predication) }))
      return predication
    },
    async deletePredication(id) {
      update((c) => ({ ...c, predications: c.predications.filter((p) => p.id !== id) }))
    },
    async saveMembre(m) {
      const membre = m.id ? m : { ...m, id: nouvelIdentifiant(m.nom, read().equipe.map((x) => x.id)) }
      update((c) => ({ ...c, equipe: upsert(c.equipe, membre) }))
      return membre
    },
    async deleteMembre(id) {
      update((c) => ({ ...c, equipe: c.equipe.filter((m) => m.id !== id) }))
    },
    async reorderEquipe(ids) {
      update((c) => ({
        ...c,
        equipe: ids.map((id) => c.equipe.find((m) => m.id === id)).filter((m): m is MembreEquipe => !!m),
      }))
    },
    async saveMotDuPasteur(motDuPasteur) {
      update((c) => ({ ...c, motDuPasteur }))
      return motDuPasteur
    },
    async saveDons(dons) {
      update((c) => ({ ...c, dons }))
      return dons
    },
    async saveContact(contact) {
      update((c) => ({ ...c, contact }))
      return contact
    },
    async reset() {
      localStorage.removeItem(STORAGE_KEY)
      return DEFAULT_CONTENT
    },
  }
}

const remplacerOuAjouter = <T extends { id: string }>(list: T[], item: T, auDebut: boolean) =>
  list.some((x) => x.id === item.id)
    ? list.map((x) => (x.id === item.id ? item : x))
    : auDebut
      ? [item, ...list]
      : [...list, item]

const CACHE_CONTENU = 'cmp-contenu-cache-v1'

/**
 * Contenu servi par l'API Symfony. La dernière version reçue est gardée dans le navigateur :
 * au retour d'un visiteur elle s'affiche immédiatement (même hors connexion) pendant que la
 * version à jour se charge.
 */
function createApiRepository(): ContentRepository {
  const lireCache = (): SiteContent | null => {
    try {
      const brut = localStorage.getItem(CACHE_CONTENU)
      return brut ? (JSON.parse(brut) as SiteContent) : null
    } catch {
      return null
    }
  }
  const ecrireCache = (contenu: SiteContent) => {
    try {
      localStorage.setItem(CACHE_CONTENU, JSON.stringify(contenu))
    } catch {
      // Stockage plein ou indisponible : le site fonctionne sans cache.
    }
  }
  const majCache = (modifier: (c: SiteContent) => SiteContent) => {
    const c = lireCache()
    if (c) ecrireCache(modifier(c))
  }

  return {
    contenuImmediat: lireCache,
    async getContent() {
      const contenu = await lireContenu()
      ecrireCache(contenu)
      return contenu
    },
    async savePredication(p) {
      const enregistree = p.id ? await modifierPredication(p) : await creerPredication(p)
      majCache((c) => ({ ...c, predications: remplacerOuAjouter(c.predications, enregistree, true) }))
      return enregistree
    },
    async deletePredication(id) {
      await supprimerPredication(id)
      majCache((c) => ({ ...c, predications: c.predications.filter((p) => p.id !== id) }))
    },
    async saveMembre(m) {
      const enregistre = m.id ? await modifierMembre(m) : await creerMembre(m, lireCache()?.equipe.length ?? 0)
      majCache((c) => ({ ...c, equipe: remplacerOuAjouter(c.equipe, enregistre, false) }))
      return enregistre
    },
    async deleteMembre(id) {
      await supprimerMembre(id)
      majCache((c) => ({ ...c, equipe: c.equipe.filter((m) => m.id !== id) }))
    },
    async reorderEquipe(ids) {
      // Les positions en base peuvent avoir des trous (membres supprimés) : on les réécrit toutes.
      await Promise.all(ids.map((id, position) => changerPosition(id, position)))
      majCache((c) => ({
        ...c,
        equipe: ids.map((id) => c.equipe.find((m) => m.id === id)).filter((m): m is MembreEquipe => !!m),
      }))
    },
    async saveMotDuPasteur(mot) {
      const enregistre = await enregistrerMotDuPasteur(mot)
      majCache((c) => ({ ...c, motDuPasteur: enregistre }))
      return enregistre
    },
    async saveDons(dons) {
      const enregistres = await enregistrerDons(dons)
      majCache((c) => ({ ...c, dons: enregistres }))
      return enregistres
    },
    async saveContact(contact) {
      const enregistre = await enregistrerContact(contact)
      majCache((c) => ({ ...c, contact: enregistre }))
      return enregistre
    },
    async reset() {
      throw new Error("Non disponible avec le serveur : le contenu se modifie section par section.")
    },
  }
}

export const repository: ContentRepository = API_URL ? createApiRepository() : createLocalRepository()
